import { LoaderIcon, Sparkles } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { podcastServices } from '../services/podcastApi';
import { podcastInputType } from '../services/podcastApi';
import { setResponse } from 'store/slices/podcastResponse';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CharacterErrors } from '../components/GetStarted/HostForm';

const GenerateDialogues = ({ hostErrors, guestErrors }: { hostErrors: Record<number, CharacterErrors>, guestErrors: Record<number, CharacterErrors> }) => {
  const [open, setOpen] = useState<boolean>(false);
  const { prompt, duration, guests, hosts } = useSelector(
    (state: RootState) => state.podcastGen
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const checkDisable = (): boolean => {
    // to check for button disability if required data is not present
    if (!prompt || duration <= 0) {
      return true;
    }

    // Check required fields for hosts
    const areHostsValid = hosts.every(
      (host, idx) => {
        if (!host.character_name || !host.voice_name) {
          return false;
        }
        return host.character_name.trim().length > 1 &&
               host.character_name.trim().length <= 50 &&
               host.voice_name.trim() !== '' &&
               !hostErrors[idx]?.character_name &&
               !hostErrors[idx]?.age &&
               !hostErrors[idx]?.voice;
      }
    );

    // Check required fields for guests
    const areGuestsValid = guests.every(
      (guest, idx) => {
        if (!guest.character_name || !guest.voice_name) {
          return false;
        }
        return guest.character_name.trim().length > 1 &&
               guest.character_name.trim().length <= 50 &&
               guest.voice_name.trim() !== '' &&
               !guestErrors[idx]?.character_name &&
               !guestErrors[idx]?.age &&
               !guestErrors[idx]?.voice;
      }
    );

    return !(areHostsValid && areGuestsValid);
  };

  const isDisable = checkDisable();

  // Sanitize characters data before sending to API
  const sanitizeCharacters = () => {
    const sanitizedHosts = hosts.map(host => ({
      ...host,
      character_name: host.character_name || '',
      voice_name: host.voice_name || ''
    }));
    
    const sanitizedGuests = guests.map(guest => ({
      ...guest,
      character_name: guest.character_name || '',
      voice_name: guest.voice_name || ''
    }));
    
    return [...sanitizedHosts, ...sanitizedGuests];
  };

  // TODO: add react query and improve loading and state management and add toasts and navigatio
  const podcastInput: podcastInputType = {
    prompt,
    duration,
    generate_text: true,
    characters: sanitizeCharacters(), // Use sanitized characters data
  };

  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);
    try {
      const res = await podcastServices.generateDialogues(podcastInput);
      console.log('API Response:', res);

      if (res.success) {
        dispatch(setResponse(res)); // Dispatch to Redux store

        console.log('Dialogues successfully fetched and dispatched to Redux.');
        setOpen(false); // Close the modal
        navigate('/ai-podcast-generator/view-podcast');
        toast.success('Podcast dialogues generated!');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button
          className="w-full sm:w-[210px] h-[50px] bg-secondary hover:bg-primary  flex items-center gap-2 justify-center"
          disabled={isDisable}
          variant={'customSecondary'}
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-[16px]">Generate Dialogues</span>
        </Button>
      </Dialog.Trigger>
      <Dialog.Overlay className="fixed inset-0 bg-background-overlay/50" />
      <Dialog.Content className="fixed text-text-primary  inset-0 flex items-center justify-center p-4 z-[100]">
        <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-[90%] sm:max-w-[480px] flex flex-col gap-1">
          <Dialog.Title className="text-2xl text-left font-bold text-primary ">
            Ready to generate dialogues?
          </Dialog.Title>
          <Dialog.Description className=" mt-2 text-left text-[15px] font-normal">
            Once the dialogues are generated you won&apos;t be able to change
            character properties. Are you sure you want to continue?
          </Dialog.Description>
          <form
            className="flex flex-row justify-end mt-6 w-full gap-2"
            onSubmit={handleSubmit}
          >
            <Button
              variant="customSecondary"
              size="sm"
              onClick={() => setOpen(false)}
              className="text-[14px] bg-secondary"
            >
              Go back
            </Button>

            <Button
              variant="customSecondary"
              size="sm"
              className="text-[14px] bg-secondary"
              disabled={loading}
            >
              {loading && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              <span>Generate</span>
            </Button>
          </form>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default GenerateDialogues;
